"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export interface RealtimeEvent {
  action: string;
  type?: string;
  channelId?: string;
  conversationId?: string;
  // presence fields ("online" | "offline"); live-stream-status events reuse
  // it for the stream status string
  memberId?: string;
  status?: string;
  // post reaction fields
  postId?: string;
  emoji?: string;
  removed?: boolean;
  // live-stream status events (Studio Live, TRA-2090) carry the event id;
  // the handler refetches rather than trusting the payload's status string.
  eventId?: string;
}

interface UseStudioWebSocketOptions {
  onMessage: (event: RealtimeEvent) => void;
  enabled?: boolean;
}

interface TokenResponse {
  token: string;
  wsEndpoint: string;
}

const HEARTBEAT_INTERVAL_MS = 30_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

async function fetchTokenAndEndpoint(): Promise<TokenResponse | null> {
  try {
    const res = await fetch("/api/realtime/token", { method: "POST" });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.token || !data.wsEndpoint) return null;
    return { token: data.token, wsEndpoint: data.wsEndpoint };
  } catch {
    return null;
  }
}

export function useStudioWebSocket({
  onMessage,
  enabled = true,
}: UseStudioWebSocketOptions): { isConnected: boolean } {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const reconnectAttemptRef = useRef(0);
  const mountedRef = useRef(true);

  // Keep the latest handler in a ref so reconnects don't re-run the effect.
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Holds the latest `connect` so the reconnect timer can call it without
  // `connect` referencing itself (a use-before-declared).
  const connectRef = useRef<(() => Promise<void>) | null>(null);

  const cleanup = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(async () => {
    if (!mountedRef.current) return;

    cleanup();

    const result = await fetchTokenAndEndpoint();
    if (!result || !mountedRef.current) return;

    const ws = new WebSocket(`${result.wsEndpoint}?token=${result.token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      setIsConnected(true);
      reconnectAttemptRef.current = 0;

      // Start heartbeat
      heartbeatRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: "ping" }));
        }
      }, HEARTBEAT_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as RealtimeEvent;
        if (data.action === "pong") return;
        onMessageRef.current(data);
      } catch {
        // Ignore malformed messages
      }
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      setIsConnected(false);

      // Exponential backoff reconnect
      const attempt = reconnectAttemptRef.current;
      const delay = Math.min(1000 * 2 ** attempt, MAX_RECONNECT_DELAY_MS);
      reconnectAttemptRef.current = attempt + 1;

      reconnectTimeoutRef.current = setTimeout(() => {
        void connectRef.current?.();
      }, delay);
    };

    ws.onerror = () => {
      // onclose fires after onerror, so reconnect is handled there
    };
  }, [cleanup]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      // Defer so the connection lifecycle's state updates don't run
      // synchronously inside the effect.
      void Promise.resolve().then(() => connect());
    }

    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [enabled, connect, cleanup]);

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const action = document.hidden ? "setAway" : "setActive";
      ws.send(JSON.stringify({ action }));
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []); // wsRef is a ref — safe empty deps

  return { isConnected };
}
